<template>
  <div class="bg-gray-07">
    <AppStoreBanner
      :shouldShown="shouldShownAppStoreBanner"
      @close="closeAppStoreBanner"
    />
    <div
      class="pb-2"
      :class="shouldShownAppStoreBanner ? 'app-store-banner-space' : null"
    >
      <Header>
        <template v-slot:left>
          <FeatherIcon icon="chevron-left" @click="goBack" />
        </template>
        <div>事業所詳細</div>
      </Header>
      <div class="bg-white border-bottom border-gray-03">
        <OfficeInformation
          :office="office"
          :officeServiceName="officeServiceName"
          :evaluationTotalAverage="evaluationTotalAverage"
          :evaluationCount="evaluationCount"
          :mostFrequentOrderTemplate="mostFrequentOrderTemplate"
          :isFavorite="isFavorite"
          :isMutedOffice="isMutedOffice"
          @favorite-change="updateFavoriteOffice"
          @change-muted-office="onClickMuteButton"
        />
      </div>
      <div class="pt-3 px-3" ref="orderList">
        <div class="order-list-header">
          <div class="text-weight-500 text-mild">お仕事一覧</div>
          <HiringOnlyOrderButton
            :hiringOnly="hiringOnly"
            @toggle-hiring-only="toggleHiringOnly"
          />
        </div>
        <template v-if="isLoading || hasOrderList">
          <div class="order-list">
            <SimpleOrderListItem
              :order="order"
              :key="order.id"
              class="d-block"
              v-for="order in orders"
            />
          </div>
        </template>
        <template v-else>
          <div class="px-3 py-3" data-testid="order-is-not-found">
            <div class="text-center">
              <Balloon />
            </div>
            <p class="text-size-14 text-mild pt-4 text-center">
              お仕事がありません
            </p>
          </div>
        </template>
      </div>
    </div>
    <AppFooter />
    <OfficeMuteModal
      :isShown="isShownMutedOfficeModal"
      :officeName="office?.name"
      @submit="onSubmitMuteOfficeModal"
      @cancel="onCloseMuteOfficeModal"
    />
  </div>
</template>

<script lang="ts">
import { orderStore } from "@/store/order";
import AppStoreBanner from "@/components/atoms/other/AppStoreBanner.vue";
import { Component, Vue, Watch } from "vue-facing-decorator";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import Header from "@/components/organisms/common/Header.vue";
import { SlimOrder } from "@/types/order/Order";
import { authStore } from "@/store/auth";
import { orderService } from "@/app/services/OrderService";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import { officeService } from "@/app/services/OfficeService";
import SimpleOrderListItem from "@/components/molecules/orders/SimpleOrderListItem.vue";
import { Office } from "@/types/office/Office";
import { appStoreBannerService } from "@/app/services/AppStoreBannerService";
import { MostFrequentOrderTemplate } from "@/types/office/MostFrequentOrderTemplate";
import OfficeInformation from "@/components/organisms/office/OfficeInformation.vue";
import { favoriteService } from "@/app/services/FavoriteService";
import { FavoriteOffice } from "@/types/office/FavoriteOffice";
import AppFooter from "@/components/organisms/common/AppFooter.vue";
import { masterStore } from "@/store/master";
import { useLoginModal } from "@/utils/modal";
import { formatFromUnixTimestamp, startOfToday } from "@/utils/date";
import { accountService } from "@/app/services/AccountService";
import OfficeMuteModal from "@/components/organisms/common/OfficeMuteModal.vue";
import HiringOnlyOrderButton from "@/components/organisms/orders/HiringOnlyOrderButton.vue";
import { h } from "vue";
import { routerPush } from "@/utils/routerPush";

const SCROLL_OFFSET = -49;

@Component({
  components: {
    FeatherIcon,
    AppStoreBanner,
    SimpleOrderListItem,
    OfficeMuteModal,
    Header,
    OfficeInformation,
    AppFooter,
    HiringOnlyOrderButton
  }
})
export default class SearchByOffice extends Vue {
  orders: SlimOrder[] = [];
  office: Office | null = null;
  isLoading = false;
  mostFrequentOrderTemplate: MostFrequentOrderTemplate | null = null;
  favoriteOffice?: FavoriteOffice | null = null;
  isMutedOffice = false;
  isShownMutedOfficeModal = false;
  evaluationTotalAverage: number | null = null;
  evaluationCount = 0;
  officeServiceName = "";
  isInitCompleted = false;
  loginModal = useLoginModal();

  /**
   * パスパラメータからオフィスIDを取得する
   */
  get officeId() {
    return Number(this.$route.params["officeId"]);
  }

  get officeName() {
    return this.office?.name || "";
  }

  get hasOrderList() {
    return this.orders.length > 0;
  }

  get shouldShownAppStoreBanner() {
    return appStoreBannerService.shouldShownBanner();
  }

  get isFavorite() {
    return !!this.favoriteOffice?.isFavorite;
  }

  get isLoggedIn() {
    return authStore.existToken;
  }

  get hiringOnly() {
    return orderStore.hiringOnly;
  }

  async created() {
    try {
      this.isLoading = true;
      this.$loading.show();
      const [orders, office] = await Promise.all([
        this.fetchUpcomingApplicableOrders(),
        officeService.fetchOffice(this.officeId)
      ]);

      this.office = office;
      this.orders = orders;
      await Promise.all([
        this.fetchMostFrequentOrderTemplate(),
        this.fetchFavoriteOffice(),
        this.fetchMutedOffice(),
        this.fetchEvaluations(),
        this.fetchOfficeServiceName()
      ]);
    } catch (error) {
      await showErrorModal(this, error as Error);
    } finally {
      this.isLoading = false;
      this.$loading.hide();
      this.isInitCompleted = true;
    }
  }

  @Watch("isInitCompleted")
  handleInitCompleted() {
    const hash = this.$route.hash;
    if (hash) {
      this.$nextTick(() => {
        const refName = hash.substr(1);
        const target = this.$refs[refName] as HTMLElement;
        if (target) {
          window.scrollTo(0, target.offsetTop + SCROLL_OFFSET);
        }
      });
    }
  }

  /**
   * 今日以降で申し込み可能な案件一覧を取得する
   */
  async fetchUpcomingApplicableOrders() {
    return await orderService.searchOrdersByOfficeIds({
      /* eslint-disable @typescript-eslint/naming-convention */
      office__in: String(this.officeId),
      date__gte: formatFromUnixTimestamp(startOfToday(), "yyyy-MM-dd"),
      service_name: this.isLoggedIn
        ? authStore.workerService.serviceName
        : undefined,
      include_closed: !this.hiringOnly
      /* eslint-enable */
    });
  }

  /**
   * 案件がクリックされた時の処理
   * @param orderId 案件ID
   */
  async onOrderClick(orderId: number) {
    await this.$router.push({
      name: "OrdersDetail",
      params: { orderId: `${orderId}` }
    });
  }

  async fetchOfficeServiceName() {
    this.officeServiceName =
      (await masterStore.getServiceNameById(this.office!.serviceTypeNumber)) ||
      "";
  }

  async fetchMostFrequentOrderTemplate() {
    this.mostFrequentOrderTemplate = await officeService.fetchMostFrequentOrderTemplate(
      this.officeId
    );
  }

  async fetchEvaluations() {
    const evaluation = await officeService.fetchTotalAvgEvaluationsByWorker(
      this.officeId
    );
    this.evaluationCount = evaluation.count;
    this.evaluationTotalAverage = evaluation.totalAverage;
  }

  async fetchFavoriteOffice() {
    // 未ログイン時は実行しない
    if (!this.isLoggedIn) return;

    const {
      results: favoriteOffices
    } = await favoriteService.fetchFavoriteOffices({
      office: this.officeId,
      limit: 1
    });

    this.favoriteOffice = favoriteOffices.length ? favoriteOffices[0] : null;
  }

  async fetchMutedOffice() {
    // 未ログイン時は実行しない
    if (!this.isLoggedIn) return;

    const { results } = await accountService.fetchMutedOffices({
      officeId: this.officeId,
      isMuted: true
    });
    this.isMutedOffice = results.length > 0;
  }

  async showLoginModal() {
    const isConfirmed = await this.loginModal.showLoginModal();
    if (isConfirmed) {
      await routerPush(this, { name: "Welcome" });
    }
  }

  async updateFavoriteOffice() {
    if (!this.isLoggedIn) {
      await this.showLoginModal();
      return;
    }
    if (this.isMutedOffice && !this.isFavorite) {
      const modalMessage = [
        "お気に入りとミュートは同時に設定できません",
        "ミュートを解除してからお気に入り登録してください"
      ];
      const messageVNode = modalMessage.map(v =>
        h("p", { class: ["text-size-14"] }, [v])
      );
      await this.$bvModal.msgBoxOk(messageVNode, {
        centered: true,
        hideHeader: true,
        okOnly: true
      });
      return;
    }

    this.$loading.show();
    try {
      const body = {
        office: this.officeId!,
        isFavorite: !this.isFavorite
      };
      this.favoriteOffice
        ? await favoriteService.updateFavoriteOffices(
            this.favoriteOffice.id,
            body
          )
        : await favoriteService.createFavoriteOffices(body);
      // refetch する
      await this.fetchFavoriteOffice();
      this.$bvToast.toast(
        this.favoriteOffice?.isFavorite
          ? "事業所のお気に入りに追加しました"
          : "事業所のお気に入りを削除しました",
        {
          toastClass: ["toast-custom", "toast-custom-thin-header-page"],
          toaster: "b-toaster-top-full",
          autoHideDelay: 2000
        }
      );
    } catch (error) {
      await showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async updateMutedOffice({ reason }: { reason?: string } = {}) {
    try {
      this.$loading.show();
      await accountService.updateMutedOffice({
        officeId: this.officeId,
        isMuted: !this.isMutedOffice,
        reason
      });
      this.isMutedOffice = !this.isMutedOffice;
      this.$bvToast.toast(
        this.isMutedOffice
          ? "事業所をミュートしました"
          : "事業所のミュートを解除しました",
        {
          toastClass: ["toast-custom", "toast-custom-thin-header-page"],
          toaster: "b-toaster-top-full",
          autoHideDelay: 2000
        }
      );
    } catch (error) {
      await showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async onClickMuteButton() {
    if (!this.isLoggedIn) {
      await this.showLoginModal();
      return;
    }
    if (this.isMutedOffice) {
      // ミュート解除の場合、ダイアログ表示なしで更新実行
      await this.updateMutedOffice();
      return;
    }
    const { canMute, message } = await accountService.canMuteOffice(
      this.officeId
    );
    if (!canMute) {
      const modalMessage = message.split("\n") || [];
      const messageVNode = modalMessage.map(v =>
        h("p", { class: ["text-size-14"] }, [v])
      );
      await this.$bvModal.msgBoxOk(messageVNode, {
        centered: true,
        hideHeader: true,
        okOnly: true
      });
      return;
    }
    this.isShownMutedOfficeModal = true;
  }

  async onSubmitMuteOfficeModal({ reason }: { reason: string }) {
    this.onCloseMuteOfficeModal();
    await this.updateMutedOffice({ reason });
  }

  async toggleHiringOnly() {
    orderStore.setHiringOnly(!this.hiringOnly);
    try {
      this.isLoading = true;
      this.$loading.show();
      const orders = await this.fetchUpcomingApplicableOrders();
      this.orders = orders;
    } catch (error) {
      await showErrorModal(this, error as Error);
    } finally {
      this.isLoading = false;
      this.$loading.hide();
    }
  }

  onCloseMuteOfficeModal() {
    this.isShownMutedOfficeModal = false;
  }

  goBack() {
    this.$router.go(-1);
  }

  closeAppStoreBanner() {
    appStoreBannerService.haveAlreadySeenBanner();
  }
}
</script>

<style lang="scss" scoped>
.app-store-banner-space {
  position: relative;
  margin-top: $app-store-banner-height;
}

.order-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.order-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 10px;
}
</style>
